import { createContext, useContext, useEffect, useState, useMemo } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=unauth, obj=auth
  const [permissions, setPermissions] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setPermissions(data.permissions || []);
    } catch (e) {
      if (e?.response?.status === 401 && localStorage.getItem("crm_session")) {
        localStorage.removeItem("crm_session");
        sessionStorage.setItem("session_expired", "1");
      }
      setUser(false);
      setPermissions([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("crm_session", "1");
    await load();
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
        console.error("Logout request failed (clearing session anyway):", e);
      }
    }
    localStorage.removeItem("crm_session");
    setUser(false);
    setPermissions([]);
  };

  const can = (perm) =>
    permissions.includes("*") || permissions.includes(perm);

  const value = useMemo(
    () => ({ user, permissions, login, logout, can, reload: load }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, permissions]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
